import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { expandGroupsToCategories } from "../../DisputesPage/pages/AllPage/constants/categoryGroups"
import { ADMIN_PAGE_SIZE, ADMIN_SORTABLE } from "../constants/columns"

// Chips whose value maps straight onto a query parameter of the same name.
const LIST_PARAMS = [
  "status",
  "dispute_type",
  "submission",
  "flag_reason",
  "reason_code",
  "issuer",
  "issuing_bank",
  "card_type",
  "currency",
  "processor",
  "paid_status",
  "customer",
  "card_last4",
  "processor_dispute_id",
  "user_full_name",
  "user_email",
  "business_account",
  "business_url"
]

// A checkbox chip holds "all"/null until something is picked; a tag chip holds an array.
// Both collapse to CSV, and an untouched chip is left out of the query entirely.
const toCsv = (value) => {
  if (value == null || value === "all") return undefined
  const list = Array.isArray(value) ? value : [value]
  const cleaned = list.map((v) => String(v).trim()).filter(Boolean)
  return cleaned.length ? cleaned.join(",") : undefined
}

// DropdownBlockNumber's shape: { type, min, max }.
const numberParams = (prefix, value) =>
  value?.type
    ? {
        [`${prefix}_type`]: value.type,
        [`${prefix}_min`]: value.min ?? undefined,
        [`${prefix}_max`]: value.max ?? undefined
      }
    : {}

// DropdownDateBlock's shape: [from, to] as YYYY-MM-DD, or null.
const dateParams = (prefix, value) =>
  Array.isArray(value) && (value[0] || value[1])
    ? { [`${prefix}_from`]: value[0] || undefined, [`${prefix}_to`]: value[1] || undefined }
    : {}

export const buildAdminDisputesParams = ({ filters, page, ordering, testData }) => {
  const params = {
    page,
    page_size: ADMIN_PAGE_SIZE,
    ordering,
    test_data: testData
  }

  LIST_PARAMS.forEach((key) => {
    const value = toCsv(filters[key])
    if (value !== undefined) params[key] = value
  })

  // The Category chip speaks in the four network groups; the backend wants the
  // fine-grained category slugs, exactly as the merchant list sends them.
  const categories = toCsv(filters.reason_category)
  if (categories) params.reason_category = expandGroupsToCategories(categories.split(",")).join(",")

  // Country lives under its own chip key so it does not collide with the shared
  // `country` options in filterOptionsData; the backend parameter is still `country`.
  const country = toCsv(filters.card_country)
  if (country) params.country = country

  const assignee = toCsv(filters.assignee)
  if (assignee) params.assigned_to = assignee

  return {
    ...params,
    ...numberParams("amount", filters.amount),
    ...numberParams("enrichment", filters.enrichment),
    ...dateParams("date", filters.date),
    ...dateParams("updated", filters.updated)
  }
}

// Page, ordering and the refetch that ties them to the filter state. Kept out of the page
// so it reads as layout rather than query plumbing (the merchant list's useDisputesQuery).
export const useAdminDisputesQuery = ({ filters, testData, pinTick, fetchDisputes }) => {
  const [page, setPage] = useState(1)
  const [ordering, setOrdering] = useState("priority")
  const lastParamsRef = useRef(null)

  const params = useMemo(
    () => buildAdminDisputesParams({ filters, page, ordering, testData }),
    [filters, page, ordering, testData]
  )

  // One string over the whole param set: the effect then refires on any filter change
  // without listing each chip.
  const paramsKey = JSON.stringify(params)

  useEffect(() => {
    const data = JSON.parse(paramsKey)
    lastParamsRef.current = data
    fetchDisputes(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey, pinTick])

  // Narrowing the list invalidates the page you were on: page 7 of a 3-page result is empty.
  const resetPage = useCallback(() => setPage(1), [])

  // antd fires this for both pagination and header-sort changes.
  const handleChangeTable = useCallback(
    (pagination, _filters, sorter) => {
      if (pagination?.current && pagination.current !== page) setPage(pagination.current)

      let next = "priority"
      const field = sorter?.columnKey || sorter?.field
      if (sorter?.order && field && ADMIN_SORTABLE.has(field)) {
        next = sorter.order === "ascend" ? field : `-${field}`
      }
      if (next !== ordering) {
        setOrdering(next)
        setPage(1)
      }
    },
    [page, ordering]
  )

  return { page, setPage, resetPage, ordering, handleChangeTable, lastParamsRef }
}

export default useAdminDisputesQuery
